import React, { useEffect, useState } from "react";
import {
    Alert,
    CircularProgress,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import Button from "../../Common/Button";
import { setSnackBar } from "../../../store/common/commonSlice";
import {
    useGetAffiliateSettingsQuery,
    useUpdateAffiliateSettingsMutation,
} from "../../../store/shopAdmin/shopAdminApis";
import { rupees } from "./affiliateUtils";

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const AffiliateProgramSettings = () => {
    const dispatch = useDispatch();
    const { data, isFetching, error } = useGetAffiliateSettingsQuery();
    const [save, { isLoading }] = useUpdateAffiliateSettingsMutation();
    const [form, setForm] = useState(null);

    useEffect(() => {
        if (data) setForm({ ...data });
    }, [data]);

    if (error) return <Alert severity="error">{error?.data?.message || "Failed to load settings"}</Alert>;
    if (isFetching || !form) return <CircularProgress />;

    const set = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const handleSave = async () => {
        const body = {
            program_enabled: Boolean(form.program_enabled),
            reward_type: form.reward_type,
            reward_percent: num(form.reward_percent) ?? 0,
            reward_flat_amount: num(form.reward_flat_amount) ?? 0,
            min_order_amount: num(form.min_order_amount) ?? 0,
            credit_trigger: form.credit_trigger,
            block_self_referral: Boolean(form.block_self_referral),
            min_withdrawal_amount: num(form.min_withdrawal_amount) ?? 500,
            rules_text: form.rules_text || "",
        };
        if (body.reward_type === "percent" && (body.reward_percent <= 0 || body.reward_percent > 100)) {
            return dispatch(
                setSnackBar({ open: true, message: "Reward % must be between 0 and 100", severity: "error" }),
            );
        }
        if (body.reward_type === "flat" && body.reward_flat_amount <= 0) {
            return dispatch(setSnackBar({ open: true, message: "Enter the flat reward amount", severity: "error" }));
        }
        try {
            await save(body).unwrap();
            dispatch(
                setSnackBar({
                    open: true,
                    message: "Program settings saved. They apply to orders placed from now on.",
                    severity: "success",
                }),
            );
        } catch (e) {
            dispatch(
                setSnackBar({ open: true, message: e?.data?.message || "Could not save settings", severity: "error" }),
            );
        }
    };

    const rewardText =
        form.reward_type === "flat" ? rupees(num(form.reward_flat_amount)) : `${num(form.reward_percent) || 0}%`;

    return (
        <Stack spacing={2} sx={{ maxWidth: 820 }}>
            <Alert severity="info">
                Reward changes apply to orders placed after you save. Withdrawal minimums apply to new requests.
                Settings changes are saved in the audit log. The first-order offer for referred customers is in Shop
                Settings.
            </Alert>

            <Paper sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={700}>
                        Referral program
                    </Typography>
                    <FormControlLabel
                        control={<Switch checked={Boolean(form.program_enabled)} onChange={set("program_enabled")} />}
                        label={form.program_enabled ? "On" : "Off"}
                    />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    When off, the app shows the program as paused and no new rewards are created. Rewards already
                    pending still follow their own rule.
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Reward type"
                            value={form.reward_type}
                            onChange={set("reward_type")}
                        >
                            <MenuItem value="percent">Percent of the order</MenuItem>
                            <MenuItem value="flat">Flat amount</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {form.reward_type === "percent" ? (
                            <TextField
                                fullWidth
                                type="number"
                                label="Reward %"
                                value={form.reward_percent ?? ""}
                                onChange={set("reward_percent")}
                                helperText="Of what the customer paid, excluding delivery"
                            />
                        ) : (
                            <TextField
                                fullWidth
                                type="number"
                                label="Reward ₹"
                                value={form.reward_flat_amount ?? ""}
                                onChange={set("reward_flat_amount")}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Minimum order ₹"
                            value={form.min_order_amount ?? ""}
                            onChange={set("min_order_amount")}
                            helperText="After discounts, excluding delivery"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Credit the reward when the order is"
                            value={form.credit_trigger}
                            onChange={set("credit_trigger")}
                        >
                            <MenuItem value="delivered">Delivered (recommended)</MenuItem>
                            <MenuItem value="paid">Paid</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={Boolean(form.block_self_referral)}
                                    onChange={set("block_self_referral")}
                                />
                            }
                            label="Block self-referral (same mobile or same phone)"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Minimum withdrawal ₹"
                            value={form.min_withdrawal_amount ?? ""}
                            onChange={set("min_withdrawal_amount")}
                            helperText="Minimum balance eligible for a weekly manual payout request"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Alert severity="info" sx={{ height: "100%", alignItems: "center" }}>
                            Withdrawals are reviewed, batched, and paid manually once a week. Admin records a bank
                            transfer reference for each payment.
                        </Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Rules shown in the app"
                            value={form.rules_text || ""}
                            onChange={set("rules_text")}
                            inputProps={{ maxLength: 2000 }}
                            helperText="Shown on the Refer & Earn screen under How it works"
                        />
                    </Grid>
                </Grid>
                <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                    Affiliates earn {rewardText} when someone they referred buys for{" "}
                    {rupees(num(form.min_order_amount))} or more. It moves to their wallet when the order is{" "}
                    {form.credit_trigger === "paid" ? "paid" : "delivered"}, and is reversed if the order is cancelled
                    or returned.
                </Typography>
            </Paper>

            <Stack direction="row" justifyContent="flex-end">
                <Button
                    fullWidth={false}
                    variant="contained"
                    onClick={handleSave}
                    loading={isLoading}
                    sx={{ minWidth: 160 }}
                >
                    Save settings
                </Button>
            </Stack>
        </Stack>
    );
};

export default AffiliateProgramSettings;
